import React, {useState,  useEffect, forwardRef, useImperativeHandle} from 'react';
import TextField from '@mui/material/TextField';
import {useTranslation} from "react-i18next";
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import {observer} from 'mobx-react';
import {useGlobalStorage} from './mobx/GlobalStorage.js';
import {latIsValid, longIsValid} from './Util';
import MapDialog from './MapDialog';
import {COMM_OK} from './WsR2.js';

/* -------------------------------------------------------------------------- */

export function roundToFive(num) {    
	return +(Math.round(num + "e+5")  + "e-5");
}

export function getCoordStr(val) {
	if (val !== 0)
		return roundToFive(val / 1000000.0);

	return "";
}

export function gpsPosString(gps) {
	let str = '-';

	if (!gps || !gps.pos)
		return str;

	if (!gps.pos.latitude || !gps.pos.longitude)
		return str;

	return roundToFive(gps.pos.latitude) + ', ' + roundToFive(gps.pos.longitude) + ' (' + gps.pos.accuracy.toFixed(0) + ' m)';
}

export function gpsPosColor(gps) {
	if (!gps || !gps.pos)
		return 'mistyrose';

	if (!gps.pos.accuracy)
		return 'white';

	if (gps.pos.accuracy < 9)
		return 'honeydew'; 

	return 'lightyellow';
}

/* -------------------------------------------------------------------------- */

let globMapOpen = false;

const Geoloc = observer(forwardRef((props, ref) => {
	const gStorage = useGlobalStorage();

	const [latStr, setLatStr] = useState(getCoordStr(gStorage.add.obs.lat_obs));
	const [latValid, setLatValid] = useState(false);
	const [longStr, setLongStr] = useState(getCoordStr(gStorage.add.obs.long_obs));
	const [longValid, setLongValid] = useState(false);
	const [mapOpen, setMapOpen] = useState(false);
	const [gpsParams, setGpsParams] = useState(null);

	const {t} = useTranslation('obs');

	let timeout;

	useEffect(() => {
		updateGps();

		const id = setInterval(updateGps, 1000);

		return () => {
			clearInterval(id);
		};
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		let _lat_valid, _lat_str, _long_valid, _long_str;

		_lat_str = getCoordStr(gStorage.add.obs.lat_obs);
		_lat_valid = latIsValid(_lat_str); 
		setLatValid(_lat_valid);
		if (_lat_valid && _lat_str !== latStr) {
			setLatStr(_lat_str);
		}

		_long_str = getCoordStr(gStorage.add.obs.long_obs);
		_long_valid = longIsValid(_long_str);
		setLongValid(_long_valid);
		if (_long_valid && _long_str !== longStr) {
			setLongStr(_long_str);
		}
		// eslint-disable-next-line
	}, [gStorage.add.obs.lat_obs, gStorage.add.obs.long_obs]);

	useEffect(() => {
		globMapOpen = mapOpen;
		if (mapOpen) {
			updateGps();
		}
		// eslint-disable-next-line
	}, [mapOpen]);

	function updateGps() {
// 		console.log('Geoloc, pos', globMapOpen);

		if (globMapOpen)
			return;

		setGpsParams({
			color: gpsPosColor(gStorage.gps), 
			pos: gpsPosString(gStorage.gps)
		});
	}

	function geolocRxWs(status, obj) {
		gStorage.add.setCityFetchInProg(false);

		if (status !== COMM_OK) {
			alert('geolocRxWs: error', status);		
			return;
		}

// 		console.log('geolocRxWs: Rx:', obj);

		if (obj.cmd === 'geo') {
			if (obj.op === 'city') {
				if (obj.result === 'OK') {
					if (obj.data.city === 'not found') {
						console.log('geolocRxWs: city for coordinates not found!');
					} else {
// 						console.log('geolocRxWs: city', obj.data.city.fi);
						props.cityChange(null, obj.data.city);
					}
				} else {
					console.error('geolocRxWs: city query failed! Result: ', obj.result, obj.data.desc);
				}
			}
		} else {
			console.error('geolocRxWs: unknown command', obj.cmd);
		}
	}

	function setObsPos(coord, solve_addr, isAuto) {
		let lat, long;

		lat = Number(coord.latitude);
		long = Number(coord.longitude);

		gStorage.add.setPos(Math.round(lat * 1000000.0), Math.round(long * 1000000.0));
		gStorage.add.setWeather(null);

// 		console.log('setObsPos ', coord, solve_addr, gStorage.add.addrFetchInProg, lat, long, isAuto);

		gStorage.add.setCoordAutoChanged(isAuto);

		if (!gStorage.conn.online || lat === 0 || long === 0 || 
		    Number.isNaN(lat) || Number.isNaN(long) || isNaN(lat) || isNaN(long) || 
		    !solve_addr || gStorage.add.addrFetchInProg || gStorage.add.cityFetchInProg)
			return;

		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'geo',
			op: 'city',
			data: {
				coord: {
					lat: gStorage.add.obs.lat_obs,
					long: gStorage.add.obs.long_obs,
				},
				city_only_allowed: 0
			},
		};

// 		console.log('setObsPos ', msg);

		gStorage.add.setCityFetchInProg(true);
		gStorage.conn.ws.send(msg, geolocRxWs);

		if (gStorage.add.start_d_valid && gStorage.add.start_t_valid) {
			props.askWeather(gStorage.add.obs.lat_obs, gStorage.add.obs.long_obs, gStorage.add.start_d_str, gStorage.add.start_t_str, gStorage.add.obs.tz);
		}
	}

	function coord2Obs(lat, long, isAuto) {
// 		console.log('coord2Obs', lat, long);

		let lat_str = getCoordStr(lat * 1000000.0), long_str = getCoordStr(long * 1000000.0);
		let lat_valid, long_valid;

		lat_valid = latIsValid(lat_str);
		long_valid = longIsValid(long_str);

		if (lat_valid && long_valid) {
			setLatStr(lat_str);
			setLatValid(true);

			setLongStr(long_str);
			setLongValid(true);

			setObsPos({latitude: lat_str, longitude: long_str}, true, isAuto);
		}
	}

	function gpsPos2Obs() {
		coord2Obs(gStorage.gps.pos.latitude, gStorage.gps.pos.longitude, true);
	}

	function latChange(event) {
		let valid = latIsValid(event.target.value);

		setLatStr(event.target.value);
		setLatValid(valid);

		if (valid && longValid) {
			setObsPos({latitude: event.target.value, longitude: longStr}, false, false);
		}
	}

	function longChange(event) {
		let valid = longIsValid(event.target.value);

		setLongStr(event.target.value);
		setLongValid(valid);

		if (valid && latValid) {
			setObsPos({latitude: latStr, longitude: event.target.value}, false, false);
		}
	}

	function openMap() {
		setMapOpen(true);
	}

	function setPos(pos) {
		coord2Obs(pos.lat, pos.lng, true);
	}

	function closeMap() {
		setMapOpen(false);
	}

	function delayedAddressQuery(e) {
// 		console.log('delayedAddressQuery');

		if (timeout)
			clearTimeout(timeout);

      		timeout = setTimeout(() => {setObsPos({latitude: latStr, longitude: longStr}, true, false);}, 300);
	}

	function setBirdPos(coord) {
		gStorage.add.setBirdPos(Math.round(Number(coord.lat) * 1000000.0), Math.round(Number(coord.lng) * 1000000.0));
	}

	useImperativeHandle(ref, () => ({
		gpsPos2Obs,
	}));

	const coordBg = gStorage.add.coordAutoChanged ? '#f4fff4' : 'white';

	return (<div>
			<Grid size={{xs: 12}} align={'center'}>
				<TextField label={t('gps.position')} size="small" variant="outlined" 
				    style={{width: 300, margin: 8, backgroundColor: gpsParams ? gpsParams.color : 'white'}}
				    value={gpsParams ? gpsParams.pos : ''} />
			</Grid>

			<Grid size={{xs: 12}}>
				<Button 
					variant="contained" 
					color="primary" 
					size="small" 
					style={{width:143, margin: 8}} 
					onClick={gpsPos2Obs}
					disabled={gStorage.add.addrFetchInProg || gStorage.add.cityFetchInProg}>
						{t('gps.update')}
				</Button>

				<Button 
					variant="contained" 
					color="primary" 
					size="small" 
					style={{width:143, margin: 8}} 
					disabled={!gStorage.conn.online}
					onClick={openMap}>
						{t('gps.map')}
				</Button>
			</Grid>

			<Grid size={{xs: 12}}>
				<TextField 
					id="lat_obs" 
					label={t('gps.lat')} 
					size="small" 
					variant="outlined" 
					style={{width: 143, margin: 8, backgroundColor: coordBg}} 
					value={latStr} 
					onKeyUp={delayedAddressQuery}
					onKeyDown={() => {if (timeout) clearTimeout(timeout);}}
					onChange={latChange}
					error={!latValid}/>

				<TextField id="long_obs" 
					label={t('gps.long')} 
					size="small" 
					variant="outlined" 
					style={{width: 143, margin: 8, backgroundColor: coordBg}} 
					value={longStr}
					onKeyUp={delayedAddressQuery}
					onKeyDown={() => {if (timeout) clearTimeout(timeout);}}
					onChange={longChange} 
					error={!longValid}/>
			</Grid>

			<MapDialog 
				mapOpen={mapOpen} 
				onClose={closeMap} 
				pos={[gStorage.add.obs.long_obs / 1000000.0, gStorage.add.obs.lat_obs / 1000000.0, gStorage.add.obs.long_tgt / 1000000.0, gStorage.add.obs.lat_tgt / 1000000.0]} 
				setPos={setPos} 
				setBirdPos={setBirdPos}
				date={props.start_d_valid ? props.start_d_str : ''} 
				time={props.start_t_valid ? props.start_t_str : ''}/>
		</div>
	);
}));

export default Geoloc;
